import React, { useState } from "react";
import "./TemplateHelp.css";

/**
 * TemplateHelp Component
 * Displays helpful information about using templates, merge fields, and customization
 */
export default function TemplateHelp({ onClose }) {
  const [activeTab, setActiveTab] = useState("merge-fields");

  return (
    <div className="template-help-modal">
      <div className="template-help-header">
        <h2>📖 Template Customization Guide</h2>
        <button className="help-close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="template-help-tabs">
        <button
          className={`help-tab ${activeTab === "merge-fields" ? "active" : ""}`}
          onClick={() => setActiveTab("merge-fields")}
        >
          🔖 Merge Fields
        </button>
        <button
          className={`help-tab ${activeTab === "colors" ? "active" : ""}`}
          onClick={() => setActiveTab("colors")}
        >
          🎨 Change Colors
        </button>
        <button
          className={`help-tab ${activeTab === "images" ? "active" : ""}`}
          onClick={() => setActiveTab("images")}
        >
          🖼️ Replace Images
        </button>
      </div>

      <div className="template-help-content">
        {activeTab === "merge-fields" && (
          <div className="help-section">
            <h3>How to Use Merge Fields</h3>
            <p>
              Merge fields are placeholders that get automatically replaced with
              real data when you send emails. They look like this:{" "}
              <code>{"{{firstName}}"}</code>
            </p>

            <div className="help-steps">
              <div className="help-step">
                <span className="step-number">1</span>
                <div>
                  <strong>Insert from Dropdown</strong>
                  <p>
                    Click the "Merge Fields ▾" button in the editor toolbar and
                    select a field to insert it at your cursor position.
                  </p>
                </div>
              </div>

              <div className="help-step">
                <span className="step-number">2</span>
                <div>
                  <strong>Available Fields</strong>
                  <ul>
                    <li>
                      <code>{"{  {firstName}}"}</code> - Customer's first name
                    </li>
                    <li>
                      <code>{"{{lastName}}"}</code> - Customer's last name
                    </li>
                    <li>
                      <code>{"{{email}}"}</code> - Customer's email address
                    </li>
                    <li>
                      <code>{"{{businessName}}"}</code> - Your business name
                    </li>
                    <li>
                      <code>{"{{businessEmail}}"}</code> - Your business email
                    </li>
                    <li>
                      <code>{"{{shopName}}"}</code> - Your shop name
                    </li>
                    <li>
                      <code>{"{{productName}}"}</code> - Product name
                    </li>
                    <li>
                      <code>{"{{productDescription}}"}</code> - Product details
                    </li>
                    <li>
                      <code>{"{{productPrice}}"}</code> - Product price (use
                      with $)
                    </li>
                    <li>
                      <code>{"{{discountCode}}"}</code> - Promotional code
                    </li>
                    <li>
                      <code>{"{{discountAmount}}"}</code> - Discount percentage
                    </li>
                  </ul>
                </div>
              </div>

              <div className="help-step">
                <span className="step-number">3</span>
                <div>
                  <strong>Automatic Replacement</strong>
                  <p>
                    When you send the email, these fields will be replaced with
                    actual data. For example: <code>{"{{firstName}}"}</code>{" "}
                    becomes "Sarah"
                  </p>
                </div>
              </div>
            </div>

            <div className="help-example">
              <strong>Example:</strong>
              <div className="example-code">
                Hi <code>{"{{firstName}}"}</code>, check out our new{" "}
                <code>{"{{productName}}"}</code> for only $
                <code>{"{{productPrice}}"}</code>!
              </div>
              <div className="example-result">
                <strong>Becomes:</strong> Hi Sarah, check out our new Spa
                Package for only $99!
              </div>
            </div>
          </div>
        )}

        {activeTab === "colors" && (
          <div className="help-section">
            <h3>How to Change Template Colors</h3>

            <div className="help-steps">
              <div className="help-step">
                <span className="step-number">1</span>
                <div>
                  <strong>Select Text or Element</strong>
                  <p>
                    Click and drag to select the text or click on a
                    button/section you want to change.
                  </p>
                </div>
              </div>

              <div className="help-step">
                <span className="step-number">2</span>
                <div>
                  <strong>Use Toolbar Color Picker</strong>
                  <p>
                    In the TinyMCE toolbar, find these buttons:
                    <ul>
                      <li>
                        <strong>Text color</strong> (A with color bar) - Changes
                        text color
                      </li>
                      <li>
                        <strong>Background color</strong> (paint bucket icon) -
                        Changes background color
                      </li>
                    </ul>
                  </p>
                </div>
              </div>

              <div className="help-step">
                <span className="step-number">3</span>
                <div>
                  <strong>Advanced: Edit HTML Source</strong>
                  <p>
                    For more control, click "Tools" → "Source code" in the
                    editor toolbar. Look for style attributes:
                  </p>
                  <div className="code-example">
                    <code>
                      style="background-color: #667eea; color: #ffffff;"
                    </code>
                  </div>
                  <p>
                    Common color codes:
                    <ul>
                      <li>#667eea - Purple/Blue</li>
                      <li>#ff6b6b - Red</li>
                      <li>#51cf66 - Green</li>
                      <li>#ffd43b - Yellow</li>
                      <li>#339af0 - Blue</li>
                      <li>#ffffff - White</li>
                      <li>#000000 - Black</li>
                    </ul>
                  </p>
                </div>
              </div>
            </div>

            <div className="help-tip">
              💡 <strong>Tip:</strong> Use your brand colors consistently
              throughout the template for a professional look!
            </div>
          </div>
        )}

        {activeTab === "images" && (
          <div className="help-section">
            <h3>How to Replace Template Images</h3>

            <div className="help-steps">
              <div className="help-step">
                <span className="step-number">1</span>
                <div>
                  <strong>Click on Existing Image</strong>
                  <p>
                    In the editor, click on any placeholder image (gray boxes
                    with "via.placeholder.com" URLs).
                  </p>
                </div>
              </div>

              <div className="help-step">
                <span className="step-number">2</span>
                <div>
                  <strong>Upload New Image</strong>
                  <p>Two ways to replace images:</p>
                  <ul>
                    <li>
                      <strong>Drag & Drop:</strong> Drag an image file directly
                      into the editor
                    </li>
                    <li>
                      <strong>Image Button:</strong> Click the "Image" button in
                      toolbar → "Upload" tab → Choose file
                    </li>
                    <li>
                      <strong>Browse Existing:</strong> Click "Image Browser"
                      button in toolbar to select from uploaded images
                    </li>
                  </ul>
                </div>
              </div>

              <div className="help-step">
                <span className="step-number">3</span>
                <div>
                  <strong>Recommended Image Sizes</strong>
                  <ul>
                    <li>
                      <strong>Logo:</strong> 150-200px wide (transparent
                      background PNG)
                    </li>
                    <li>
                      <strong>Hero Image:</strong> 600px wide (maintain aspect
                      ratio)
                    </li>
                    <li>
                      <strong>Product Images:</strong> 300-400px wide
                    </li>
                    <li>
                      <strong>Icons:</strong> 50-100px square
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="help-tip">
              💡 <strong>Tip:</strong> Keep images under 500KB each for faster
              email loading. Use JPG for photos, PNG for logos/graphics.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
